import { pdf } from "@react-pdf/renderer";
import React, { useCallback, useEffect, useState } from "react";
import ApprovalLetter from "../PdfTemplates/ApprovalLetter";
import { Application } from "../../types";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { ExhibitorBadge } from "../PdfTemplates/ExhibitorBadge";
import StandPosterDocument from "../StandPoster";
import QrCode from "qrcode";

type ZipModalProps = {
  onClose: () => void;
  applications: Application[];
  letters?: boolean;
};

export const ZipModal: React.FC<ZipModalProps> = ({
  onClose,
  applications,
  letters = false,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<Blob>();

  const generateZipWithLetters = useCallback(
    async (students: Application[]) => {
      const zip = new JSZip();

      for (const student of students) {
        const qrData = QrCode.toDataURL(student.businessLogo);
        const blob = await pdf(
          <ApprovalLetter
            fullName={student.fullName}
            projectTitle={student.businessName}
            eventName="Feria de Emprendedores 2025 UAT-UAMVH"
            eventDates="27 de Noviembre de 2025"
          />
        ).toBlob();

        const blob2 = await pdf(
          <StandPosterDocument
            fullName={student.fullName}
            businessLogo={student.businessLogo}
            businessName={student.businessName}
            qrData={qrData}
          />
        ).toBlob();

        // Agrega el PDF al ZIP
        zip.file(`Carta-${student.fullName}-${student.studentId}.pdf`, blob);
        zip.file(`Poster-${student.fullName}-${student.studentId}.pdf`, blob2);
      }

      // Genera el ZIP y lo descarga
      const zipBlob = await zip.generateAsync({ type: "blob" });
      setBlobUrl(zipBlob);
    },
    []
  );

  const generateZipWithBadges = useCallback(async (students: Application[]) => {
    const zip = new JSZip();

    const chunkSize = 4;
    const chunks: Application[][] = [];
    for (let i = 0; i < students.length; i += chunkSize) {
      chunks.push(students.slice(i, i + chunkSize));
      // do whatever
    }

    for (const chunk of chunks) {
      const blob = await pdf(<ExhibitorBadge applications={chunk} />).toBlob();

      // Agrega el PDF al ZIP
      zip.file(`Gafetes-${chunks.indexOf(chunk) + 1}.pdf`, blob);
    }

    // Genera el ZIP y lo descarga
    const zipBlob = await zip.generateAsync({ type: "blob" });
    setBlobUrl(zipBlob);
  }, []);

  useEffect(() => {
    (async () => {
      if (applications.length > 0) {
        setLoading(true);
        if (!letters) await generateZipWithBadges(applications);
        else await generateZipWithLetters(applications);
        setLoading(false);
      }
    })();
  }, [applications, generateZipWithLetters, generateZipWithBadges, letters]);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-fadeIn">
        {/* Botón de cierre */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
          aria-label="Cerrar"
        >
          ✕
        </button>

        <div className="text-center mb-6">
          <h4 className="text-xl font-semibold text-gray-800">
            Cartas de Aprobación
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Descarga tu carta oficial de aprobación en formato PDF.
          </p>
        </div>

        <button
          disabled={loading}
          onClick={() =>
            saveAs(
              blobUrl!,
              letters ? "documentos-aprobacion.zip" : "gafetes.zip"
            )
          }
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5 px-4 rounded-xl shadow transition disabled:opacity-70"
        >
          {loading ? "Generando PDF..." : "Descargar Archivo ZIP"}
        </button>

        {/* Pie del modal */}
        <p className="text-xs text-center text-gray-400 mt-5">
          Este documento es emitido por la coordinación de eventos
          institucionales.
        </p>
      </div>
    </div>
  );
};
